/**
 * RiskHeatmap Component
 * Displays a 3x4 grid showing vendor distribution by risk level and business criticality
 *
 * Layout uses CSS Grid for clean, maintainable alignment:
 * - 6 columns: [y-axis-title] [row-labels] [cell] [cell] [cell] [cell]
 * - 5 rows: [data-row] [data-row] [data-row] [x-axis-labels] [x-axis-title]
 */

import { Fragment, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@nextui-org/react";
import { HeatmapCell } from "./HeatmapCell";
import { SectionError } from "./SectionError";
import { useGetRiskHeatmap } from "../requests";
import { ROUTES } from "@/shared/constants/routes";
import type {
  RiskLevel,
  BusinessCriticality,
  RiskHeatmapResponse,
} from "../types";

// ============================================================================
// GRID CONFIGURATION - Single source of truth for all dimensions
// ============================================================================
const CELL_SIZE = 68; // Cell width/height in pixels
const GAP_SIZE = 8;   // Gap between cells in pixels

// Risk levels in display order (top to bottom: High, Medium, Low)
const riskLevels: RiskLevel[] = ["High", "Medium", "Low"];

// Business criticality levels in display order (left to right)
const criticalityLevels: BusinessCriticality[] = [
  "Low",
  "Medium",
  "High",
  "Critical",
];

/**
 * Loading skeleton for risk heatmap - uses same CSS Grid layout as actual component
 */
const RiskHeatmapSkeleton = () => (
  <div className="flex flex-col p-4 gap-4 bg-white shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_rgba(0,0,0,0.06)] rounded-lg flex-1 h-[356px]">
    <Skeleton className="h-5 w-24 rounded" />

    {/* CSS Grid Layout - matching actual component structure */}
    <div
      className="grid flex-1 place-content-center"
      style={{
        gridTemplateColumns: `24px 32px repeat(4, ${CELL_SIZE}px)`,
        gridTemplateRows: `repeat(3, ${CELL_SIZE}px) 32px 24px`,
        gap: `${GAP_SIZE}px`,
      }}
    >
      {/* Y-axis title skeleton - spans all 3 data rows */}
      <div
        className="flex items-center justify-center"
        style={{ gridRow: "1 / 4", gridColumn: "1" }}
      >
        <Skeleton className="h-16 w-3 rounded" />
      </div>

      {/* Data rows: Y-axis label + 4 cells each */}
      {[1, 2, 3].map((rowIndex) => (
        <Fragment key={rowIndex}>
          {/* Row label skeleton */}
          <div
            className="flex items-center justify-center"
            style={{ gridRow: rowIndex, gridColumn: "2" }}
          >
            <Skeleton className="h-10 w-3 rounded" />
          </div>
          {/* Cell skeletons for this row */}
          {[3, 4, 5, 6].map((colIndex) => (
            <div
              key={`cell-${rowIndex}-${colIndex}`}
              style={{ gridRow: rowIndex, gridColumn: colIndex }}
            >
              <Skeleton className="w-[68px] h-[68px] rounded-xl" />
            </div>
          ))}
        </Fragment>
      ))}

      {/* X-axis labels skeleton - row 4, columns 3-6 */}
      {[3, 4, 5, 6].map((colIndex) => (
        <div
          key={`x-label-${colIndex}`}
          className="flex items-center justify-center"
          style={{ gridRow: 4, gridColumn: colIndex }}
        >
          <Skeleton className="h-4 w-12 rounded" />
        </div>
      ))}

      {/* X-axis title skeleton - row 5, spans columns 3-6 */}
      <div
        className="flex items-center justify-center"
        style={{ gridRow: 5, gridColumn: "3 / 7" }}
      >
        <Skeleton className="h-4 w-32 rounded" />
      </div>
    </div>
  </div>
);

/**
 * Get count for a specific risk/criticality combination from matrix
 */
function getCellCount(
  matrix: RiskHeatmapResponse["matrix"],
  riskLevel: RiskLevel,
  businessCriticality: BusinessCriticality
): number {
  const cell = matrix.find(
    (c) =>
      c.risk_level === riskLevel &&
      c.business_criticality === businessCriticality
  );
  return cell?.count ?? 0;
}

export const RiskHeatmap = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useGetRiskHeatmap();

  const handleCellClick = useCallback(
    (riskLevel: RiskLevel, businessCriticality: BusinessCriticality) => {
      const riskParam = riskLevel.toLowerCase();
      const criticalityParam = businessCriticality.toLowerCase();
      navigate(
        `/${ROUTES.THIRD_PARTY_VENDORS_V2}?filter_keys=${riskParam}&business_criticality=${criticalityParam}`
      );
    },
    [navigate]
  );

  // Loading state
  if (isLoading) {
    return <RiskHeatmapSkeleton />;
  }

  // Error state - wrap in container with title matching success/skeleton state
  if (isError || !data) {
    return (
      <div className="flex flex-col p-4 gap-4 bg-white shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_rgba(0,0,0,0.06)] rounded-lg flex-1 h-[356px]">
        <h3 className="font-inter font-medium text-sm text-[#0F172B]">
          Risk Heatmap
        </h3>
        <div className="flex-1 flex items-center justify-center">
          <SectionError
            title="Failed to load risk heatmap"
            onRetry={() => refetch()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col p-4 gap-4 bg-white shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_rgba(0,0,0,0.06)] rounded-lg flex-1 h-[356px]">
      {/* Title */}
      <h3 className="font-inter font-medium text-sm text-[#0F172B]">
        Risk Heatmap
      </h3>

      {/* CSS Grid Layout - clean alignment without negative margins */}
      <div
        className="grid flex-1 place-content-center"
        style={{
          gridTemplateColumns: `24px 32px repeat(4, ${CELL_SIZE}px)`,
          gridTemplateRows: `repeat(3, ${CELL_SIZE}px) 32px 24px`,
          gap: `${GAP_SIZE}px`,
        }}
      >
        {/* Y-axis title - spans all 3 data rows, auto-centered */}
        <div
          className="flex items-center justify-center"
          style={{ gridRow: "1 / 4", gridColumn: "1" }}
        >
          <span className="transform -rotate-90 whitespace-nowrap text-xs font-medium text-[#62748E] uppercase tracking-wider">
            Risk Level
          </span>
        </div>

        {/* Data rows: Y-axis label + 4 cells each */}
        {riskLevels.map((riskLevel, rowIndex) => (
          <Fragment key={riskLevel}>
            {/* Row label */}
            <div
              className="flex items-center justify-center"
              style={{ gridRow: rowIndex + 1, gridColumn: "2" }}
            >
              <span className="transform -rotate-90 whitespace-nowrap text-xs font-medium text-[#90A1B9]">
                {riskLevel}
              </span>
            </div>
            {/* Cells for this row */}
            {criticalityLevels.map((criticality, colIndex) => (
              <div
                key={`${riskLevel}-${criticality}`}
                style={{ gridRow: rowIndex + 1, gridColumn: colIndex + 3 }}
              >
                <HeatmapCell
                  riskLevel={riskLevel}
                  businessCriticality={criticality}
                  count={getCellCount(data.matrix, riskLevel, criticality)}
                  onClick={() => handleCellClick(riskLevel, criticality)}
                />
              </div>
            ))}
          </Fragment>
        ))}

        {/* X-axis labels - row 4, columns 3-6 */}
        {criticalityLevels.map((level, colIndex) => (
          <div
            key={`x-label-${level}`}
            className="flex items-center justify-center"
            style={{ gridRow: 4, gridColumn: colIndex + 3 }}
          >
            <span className="text-xs font-medium text-[#90A1B9]">
              {level}
            </span>
          </div>
        ))}

        {/* X-axis title - row 5, spans columns 3-6 */}
        <div
          className="flex items-center justify-center"
          style={{ gridRow: 5, gridColumn: "3 / 7" }}
        >
          <span className="text-xs font-medium text-[#62748E] uppercase tracking-wider">
            Business Criticality
          </span>
        </div>
      </div>
    </div>
  );
};

export default RiskHeatmap;
